import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, loginMfaEnable, loginMfaSetup } from '../../lib/api';
import type { MfaSetup, Session } from '../../lib/types';
import { SparkInput } from '../SparkInput';
import { SparkMessage } from '../SparkMessage';
import { AuthenticatorSetup } from './AuthenticatorSetup';
import { BackupCodes } from './BackupCodes';

interface RequiredSetupProps {
  /** The setup token from the password step. */
  mfaToken: string;
  userId: string;
  /** Called with the session once the app is set up and the codes were seen. */
  onDone: (session: Session) => void;
  /** Back to the username and password form, with an optional message. */
  onCancel: (message?: string) => void;
}

/**
 * The sign-in step for an account that has to use two-step verification but
 * has not set it up: scan the QR code, confirm a code, save the backup codes.
 */
export function RequiredSetup({ mfaToken, userId, onDone, onCancel }: RequiredSetupProps) {
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState<{ session: Session; codes: string[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    loginMfaSetup(mfaToken)
      .then((result) => {
        if (!cancelled) setSetup(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'mfa_expired') {
          onCancel(err.message);
        } else {
          setFormError(err instanceof ApiError ? err.message : 'Setup could not be started. Please try again.');
        }
      });
    return () => {
      cancelled = true;
    };
    // The token is fixed for the life of this screen.
  }, [mfaToken]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    if (code.length !== 6) {
      setFieldError('Enter the 6-digit code from the app.');
      return;
    }
    setFieldError('');
    setSubmitting(true);
    try {
      const { recoveryCodes, ...session } = await loginMfaEnable(mfaToken, code);
      setFinished({ session, codes: recoveryCodes });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'mfa_expired' || err.code === 'account_disabled') {
          onCancel(err.message);
          return;
        }
        setFormError(err.message);
        setFieldError(err.fields?.code ?? '');
      } else {
        setFormError('The code could not be checked. Please try again.');
      }
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="spark-panel">
      <div className="spark-panel__content">
        {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

        {finished ? (
          <>
            <SparkMessage variant="success">Two-step verification is set up.</SparkMessage>
            <h3 className="spark-panel__heading">Save your backup codes</h3>
            <BackupCodes codes={finished.codes} userId={userId} />
            <div className="spark-mar-t-1">
              <button
                type="button"
                className="spark-btn spark-btn--lg spark-btn--primary spark-btn--block"
                onClick={() => onDone(finished.session)}
              >
                I have saved them, continue
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="spark-panel__heading">Set up two-step verification</h3>
            <p className="field-hint">
              Your agency requires a code from Google Authenticator each time you sign in. Set it up now to continue.
            </p>

            {!setup && !formError && <p className="field-hint">Preparing your QR code…</p>}

            {setup && (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <AuthenticatorSetup setup={setup} />
                <SparkInput
                  id="mfaSetupCode"
                  label="Authentication code"
                  placeholder="Example: 123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  error={fieldError}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  disabled={submitting}
                />
                <div className="spark-mar-t-1">
                  <button
                    type="submit"
                    className="spark-btn spark-btn--lg spark-btn--primary spark-btn--block"
                    disabled={submitting}
                  >
                    {submitting ? 'Checking…' : 'Verify and continue'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {!finished && (
          <div className="login-links">
            <button type="button" className="spark-btn-link" onClick={() => onCancel()}>
              Back to sign-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
